import { requestsControllerFindAll } from '@kalba/api-client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Building2,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Clock,
  CreditCard,
  Eye,
  FileCheck,
  MoreHorizontal,
  Plus,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../components/shared/card-data-table';
import { GradientAvatar } from '../../components/shared/gradient-avatar';
import { StatCard } from '../../components/shared/stat-card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { FilterPills } from '../../components/ui/filter-pills';
import { DirhamIcon, formatPrice } from '../../components/ui/price';
import { ProgressBar } from '../../components/ui/progress-bar';
import { usePermissions } from '../../hooks/use-permissions';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

// ── Status config ─────────────────────────────────────────────────────────────

type BadgeVariant = 'default' | 'primary' | 'success' | 'error' | 'warning';

interface StatusConfig {
  label: string;
  variant: BadgeVariant;
  dot: string;
  progress: number;
  progressColor: string;
  filterGroup: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  DRAFT: {
    label: 'Draft',
    variant: 'default',
    dot: 'bg-gray-400',
    progress: 5,
    progressColor: 'bg-gray-400',
    filterGroup: 'Draft',
  },
  SUBMITTED_FOR_INSPECTION: {
    label: 'Inspection Requested',
    variant: 'warning',
    dot: 'bg-warning-500',
    progress: 25,
    progressColor: 'bg-warning-500',
    filterGroup: 'Inspection',
  },
  INSPECTION_IN_PROGRESS: {
    label: 'Inspection In Progress',
    variant: 'warning',
    dot: 'bg-warning-500',
    progress: 40,
    progressColor: 'bg-warning-500',
    filterGroup: 'Inspection',
  },
  PLEDGE_STUDY_REQUEST: {
    label: 'Pledge Study',
    variant: 'primary',
    dot: 'bg-brand-400',
    progress: 50,
    progressColor: 'bg-brand-400',
    filterGroup: 'All',
  },
  APPROVAL_REQUEST: {
    label: 'Approval',
    variant: 'primary',
    dot: 'bg-brand-500',
    progress: 60,
    progressColor: 'bg-brand-500',
    filterGroup: 'All',
  },
  APPROVED: {
    label: 'Approved',
    variant: 'success',
    dot: 'bg-success-500',
    progress: 75,
    progressColor: 'bg-success-500',
    filterGroup: 'Approved',
  },
  REJECTED: {
    label: 'Rejected',
    variant: 'error',
    dot: 'bg-error-500',
    progress: 0,
    progressColor: 'bg-error-500',
    filterGroup: 'Rejected',
  },
  PAYMENT_PROCESS: {
    label: 'Payment',
    variant: 'warning',
    dot: 'bg-warning-500',
    progress: 80,
    progressColor: 'bg-warning-500',
    filterGroup: 'Payment',
  },
  PAID: {
    label: 'Paid',
    variant: 'success',
    dot: 'bg-success-500',
    progress: 90,
    progressColor: 'bg-success-500',
    filterGroup: 'All',
  },
  PERMIT_ISSUED: {
    label: 'Issued',
    variant: 'success',
    dot: 'bg-success-500',
    progress: 100,
    progressColor: 'bg-success-500',
    filterGroup: 'All',
  },
  SENT_TO_SAP: {
    label: 'SAP Synced',
    variant: 'success',
    dot: 'bg-success-500',
    progress: 100,
    progressColor: 'bg-success-500',
    filterGroup: 'All',
  },
};

const FILTER_PILLS = [
  { value: '__all__', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PAYMENT_PROCESS', label: 'Payment' },
  { value: 'SUBMITTED_FOR_INSPECTION,INSPECTION_IN_PROGRESS', label: 'Inspection' },
  { value: 'APPROVAL_REQUEST', label: 'Pending Approval' },
  { value: 'REJECTED', label: 'Rejected' },
];

const searchSchema = z.object({
  status: z.string().optional(),
  permitTypeCode: z.string().optional(),
  search: z.string().optional(),
  accountId: z.string().optional(),
  accountName: z.string().optional(),
  page: z.number().catch(1),
  limit: z.number().catch(20),
});

interface PermitListItem {
  id: string;
  referenceNo: string;
  status: string;
  requestAction: string;
  riskCode?: string | null;
  totalFee?: string | null;
  pledgeStudyFee?: string | null;
  createdAt: string;
  requestTypeConfig: { id: string; name: string; code: string };
  account: { id: string; name: string };
  contact: { id: string; fullName: string; email: string };
}

export const Route = createFileRoute('/permits/')({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('permit:view')) throw redirect({ to: '/dashboard' });
  },
  component: PermitsPage,
});

function formatDate(iso: string) {
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

// ── Status badge with dot ────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return <Badge variant="default">{status}</Badge>;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap',
        cfg.variant === 'default' && 'border-gray-200 bg-gray-50 text-gray-600',
        cfg.variant === 'primary' && 'border-brand-200 bg-brand-50 text-brand-600',
        cfg.variant === 'success' && 'border-success-200 bg-success-50 text-success-600',
        cfg.variant === 'warning' && 'border-warning-200 bg-warning-50 text-warning-600',
        cfg.variant === 'error' && 'border-error-200 bg-error-50 text-error-600',
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', cfg.dot)} />
      {cfg.label}
    </span>
  );
}

// ── List table columns ───────────────────────────────────────────────────────

const PERMIT_COLUMNS: CardDataTableColumn[] = [
  { id: 'permit', header: 'Permit Request' },
  { id: 'customer', header: 'Customer' },
  { id: 'status', header: 'Status' },
  { id: 'fee', header: 'Total Fee' },
  { id: 'progress', header: 'Progress' },
];

// grid: permit-info | customer | status | fee | progress
const PERMIT_GRID = 'minmax(180px, 1.5fr) minmax(160px, 1.2fr) 160px 110px minmax(120px, 1fr)';

// ── Stat cards ───────────────────────────────────────────────────────────────

type PermitCountStatus =
  | 'DRAFT'
  | 'SUBMITTED_FOR_INSPECTION'
  | 'INSPECTION_IN_PROGRESS'
  | 'PLEDGE_STUDY_REQUEST'
  | 'APPROVAL_REQUEST'
  | 'APPROVED'
  | 'REJECTED'
  | 'PAYMENT_PROCESS'
  | 'PAID'
  | 'PERMIT_ISSUED'
  | 'SENT_TO_SAP';

function usePermitCountStat(key: string, status?: PermitCountStatus, enabled = true) {
  return useQuery({
    queryKey: ['permits', 'stat', key],
    queryFn: () =>
      requestsControllerFindAll({
        ...(status ? { status } : {}),
        limit: 1,
        page: 1,
      }) as unknown as Promise<{ total: number } | unknown[]>,
    enabled,
    staleTime: 60_000,
    select: (d): number => {
      if (Array.isArray(d)) return (d as unknown[]).length;
      return (d as { total: number }).total ?? 0;
    },
  });
}

// ── Page ─────────────────────────────────────────────────────────────────────

function PermitsPage() {
  const navigate = useNavigate({ from: '/permits/' });
  const { can } = usePermissions();
  const search = Route.useSearch();

  const [localSearch, setLocalSearch] = useState(search.search ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (localSearch === (search.search ?? '')) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      navigate({ search: { ...search, search: localSearch || undefined, page: 1 }, replace: true });
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localSearch, search.search, navigate, search]);

  const canView = can('permit:view');
  const totalStat = usePermitCountStat('total', undefined, canView);
  const draftStat = usePermitCountStat('draft', 'DRAFT', canView);
  const inspectionStat = usePermitCountStat('inspection', 'SUBMITTED_FOR_INSPECTION', canView);
  const paymentStat = usePermitCountStat('payment', 'PAYMENT_PROCESS', canView);
  const pendingApprovalStat = usePermitCountStat('pendingApproval', 'APPROVAL_REQUEST', canView);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['permits', search],
    queryFn: () => {
      const statusParam = search.status;
      const isMultiStatus = statusParam?.includes(',');
      return requestsControllerFindAll({
        ...(isMultiStatus
          ? { statuses: statusParam }
          : statusParam
            ? {
                status: statusParam as
                  | 'DRAFT'
                  | 'SUBMITTED_FOR_INSPECTION'
                  | 'INSPECTION_IN_PROGRESS'
                  | 'PLEDGE_STUDY_REQUEST'
                  | 'APPROVAL_REQUEST'
                  | 'APPROVED'
                  | 'REJECTED'
                  | 'PAYMENT_PROCESS'
                  | 'PAID'
                  | 'PERMIT_ISSUED'
                  | 'SENT_TO_SAP',
              }
            : {}),
        ...(search.permitTypeCode ? { permitTypeCode: search.permitTypeCode } : {}),
        ...(search.search ? { search: search.search } : {}),
        ...(search.accountId ? { accountId: search.accountId } : {}),
        page: search.page,
        limit: search.limit,
      }) as unknown as Promise<{ data: PermitListItem[]; total: number } | PermitListItem[]>;
    },
  });

  const permits: PermitListItem[] = Array.isArray(data) ? data : (data?.data ?? []);
  const total: number = Array.isArray(data) ? permits.length : (data?.total ?? permits.length);

  function setFilter(key: string, value: string | undefined) {
    navigate({ search: { ...search, [key]: value, page: 1 }, replace: true });
  }

  const activeStatusPill = search.status ?? '__all__';

  return (
    <AppLayout>
      <PageHeader
        title="Permit Requests"
        description="Track and manage all permit applications across projects and customers."
        actions={
          can('permit:create') ? (
            <Button onClick={() => navigate({ to: '/permits/new' })}>
              <Plus size={16} />
              New Permit Request
            </Button>
          ) : null
        }
      />

      {/* Stats row */}
      <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
        <StatCard
          icon={<ClipboardList size={17} />}
          iconBg="bg-brand-50"
          iconColor="text-brand-500"
          label="Total Permit Requests"
          value={totalStat.data}
          loading={totalStat.isLoading}
        />
        <StatCard
          icon={<FileCheck size={17} />}
          iconBg="bg-orange-50"
          iconColor="text-orange-500"
          label="Draft Requests"
          value={draftStat.data}
          loading={draftStat.isLoading}
        />
        <StatCard
          icon={<Clock size={17} />}
          iconBg="bg-warning-50"
          iconColor="text-warning-500"
          label="Pending Inspection"
          value={inspectionStat.data}
          loading={inspectionStat.isLoading}
        />
        <StatCard
          icon={<CreditCard size={17} />}
          iconBg="bg-purple-50"
          iconColor="text-purple-500"
          label="Payment Pending"
          value={paymentStat.data}
          loading={paymentStat.isLoading}
        />
        <StatCard
          icon={<CheckCircle2 size={17} />}
          iconBg="bg-amber-50"
          iconColor="text-amber-500"
          label="Pending Approval"
          value={pendingApprovalStat.data}
          loading={pendingApprovalStat.isLoading}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        {/* Search */}
        <div className="relative min-w-64 flex-1">
          <Search
            size={15}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder="Search by reference, type, or company…"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
          />
        </div>

        {/* Status pills */}
        <FilterPills
          options={FILTER_PILLS}
          value={activeStatusPill}
          onChange={(v) => setFilter('status', v === '__all__' ? undefined : v)}
        />

        {/* Active customer filter chip */}
        {search.accountId && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 pl-2.5 pr-1.5 py-1 text-xs font-semibold text-brand-700">
            <Building2 size={11} />
            {search.accountName ?? 'Customer'}
            <button
              type="button"
              onClick={() =>
                navigate({
                  search: { ...search, accountId: undefined, accountName: undefined, page: 1 },
                  replace: true,
                })
              }
              className="flex h-4 w-4 items-center justify-center rounded-full hover:bg-brand-200 transition-colors"
              title="Clear customer filter"
            >
              <X size={9} />
            </button>
          </span>
        )}

        {/* Extra filters */}
        <button
          type="button"
          className="flex items-center gap-2 rounded-xl border border-gray-200 px-3.5 py-2 text-sm text-gray-500 font-medium hover:bg-gray-50 transition-colors"
        >
          <SlidersHorizontal size={14} />
          Filters
        </button>

        <span className="ml-auto text-xs text-gray-400">
          {total} {total === 1 ? 'permit request' : 'permit requests'}
        </span>
      </div>

      {/* List */}
      <CardDataTable
        gridTemplateColumns={PERMIT_GRID}
        columns={PERMIT_COLUMNS}
        isLoading={isLoading}
        errorContent={
          isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
              <p className="text-sm font-medium text-error-600">Failed to load permits</p>
              <p className="mt-1 text-xs text-error-500">Please refresh the page</p>
            </div>
          ) : undefined
        }
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <FileCheck size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">
              No permit requests match your search
            </p>
            <p className="mt-1 text-xs text-gray-400">Try adjusting your filters or search terms</p>
          </div>
        }
        rows={permits.map((permit): CardDataTableRowDef => {
          const cfg = STATUS_CONFIG[permit.status];
          const subtext = [permit.riskCode, permit.requestAction].filter(Boolean).join(' · ');
          return {
            id: permit.id,
            avatar: (
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-400 group-hover:bg-brand-50 group-hover:text-brand-500 transition-colors">
                <FileCheck size={18} />
              </div>
            ),
            cells: {
              permit: (
                <div className="min-w-0">
                  <p className="font-mono text-base font-bold tracking-tight text-brand-600 group-hover:text-brand-700 transition-colors">
                    {permit.referenceNo}
                  </p>
                  <p className="truncate text-xs font-medium text-gray-500 leading-snug mt-0.5">
                    {permit.requestTypeConfig.name}
                  </p>
                  {subtext && (
                    <p className="mt-0.5 text-[10px] text-gray-400 truncate">{subtext}</p>
                  )}
                </div>
              ),
              customer: (
                <div className="flex items-center gap-2.5 min-w-0">
                  <GradientAvatar name={permit.account.name} size="sm" shape="rounded" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-800">
                      {permit.account.name}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-[10px] text-gray-400">
                      <Calendar size={9} />
                      {formatDate(permit.createdAt)}
                    </p>
                  </div>
                </div>
              ),
              status: <StatusBadge status={permit.status} />,
              fee: permit.totalFee ? (
                <span className="inline-flex items-center gap-0.5 text-sm font-semibold text-gray-800">
                  <DirhamIcon className="w-[0.9em] h-[0.9em] text-gray-500" />
                  {formatPrice(permit.totalFee)}
                </span>
              ) : (
                <span className="text-xs text-gray-400 italic">—</span>
              ),
              progress:
                cfg && permit.status !== 'REJECTED' ? (
                  <ProgressBar
                    value={cfg.progress}
                    color={cfg.progressColor}
                    showLabel
                    className="w-full"
                  />
                ) : (
                  <span className="text-xs italic text-gray-400">N/A</span>
                ),
            },
            onClick: () => navigate({ to: '/permits/$permitId', params: { permitId: permit.id } }),
            actions: [
              {
                icon: <Eye size={13} />,
                label: 'View',
                onClick: (e) => {
                  e.stopPropagation();
                  navigate({ to: '/permits/$permitId', params: { permitId: permit.id } });
                },
              },
              {
                icon: <MoreHorizontal size={13} />,
                label: 'More',
                onClick: (e) => e.stopPropagation(),
              },
            ],
            expandContent: (
              <p className="text-xs text-gray-400">
                Contact:{' '}
                <span className="font-medium text-gray-600">{permit.contact.fullName}</span>
                <span className="mx-1.5 text-gray-300">·</span>
                Click to open permit detail
              </p>
            ),
          };
        })}
      />

      {/* Pagination */}
      {!isLoading && total > search.limit && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Showing {permits.length} of {total}
          </p>
          <div className="flex items-center gap-1">
            {Array.from(
              { length: Math.min(Math.ceil(total / search.limit), 5) },
              (_, i) => i + 1,
            ).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => navigate({ search: { ...search, page: p }, replace: true })}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-colors',
                  search.page === p
                    ? 'bg-brand-500 text-white shadow-sm shadow-brand-500/25'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
